import { useState, useEffect, Suspense, lazy } from 'react';
import { BrowserRouter, Routes, Route, Navigate, useLocation } from 'react-router-dom';
import { useSelector } from '@legendapp/state/react';
import { Analytics } from '@vercel/analytics/react';
import * as Sentry from '@sentry/react';
import { auth$, fetchCards, fetchDecks, languages$, fetchLanguages, fetchXPData, fetchSettings } from './store';
import { ChatProvider } from './context/ChatContext';
import Navigation from './components/Navigation';

// Lazy-loaded tab components for code splitting
// ChatTab is eager-loaded as it's the primary/default tab
import ChatTab from './components/tabs/ChatTab';
const DiscoveryTab = lazy(() => import('./components/tabs/DiscoveryTab'));
const ReviewTab = lazy(() => import('./components/tabs/ReviewTab'));
const ProfileTab = lazy(() => import('./components/tabs/ProfileTab'));
const ProofreadTab = lazy(() => import('./components/tabs/ProofreadTab'));

// Lazy-loaded pages and heavy components
const LoginPage = lazy(() => import('./pages/LoginPage'));
const Onboarding = lazy(() => import('./components/Onboarding'));
const PaymentSuccess = lazy(() => import('./components/PaymentSuccess'));
const SharedMellowChatView = lazy(() => import('./components/SharedMellowChatView'));

// Eagerly loaded - needed for core app shell
import XPAnimationPortal from './components/XPAnimationPortal';
import ErrorBoundary from './components/ErrorBoundary';
import { initHotjar, initClarity, initPostHog, initAmplitude, initGA4, captureCrossDomainParams, trackPageView } from './lib/analytics';
import { initFeaturebase, identifyFeaturebaseUser, clearFeaturebaseIdentity } from './lib/featurebase';
import { initNativeFeatures, hideSplashScreen } from './lib/native';
import { useKeyboardScrollAssist } from './hooks/useKeyboardScrollAssist';
import { initDeepLinks } from './lib/deeplink';
import { Capacitor } from '@capacitor/core';
import PwaInstall from '@khmyznikov/pwa-install/dist/pwa-install.react';
import { initPushNotifications } from './lib/push';
import './lib/pwa-analytics'; // Track PWA installation and launch
import './i18n';

// Protected route wrapper
function ProtectedRoute({ children }) {
  const isAuthenticated = useSelector(() => auth$.isAuthenticated.get());
  const isLoading = useSelector(() => auth$.isLoading.get());

  if (isLoading) {
    return (
      <div className="loading-screen">
        <span className="loading-icon">🍃</span>
        <span className="loading-text">Loading...</span>
      </div>
    );
  }

  return isAuthenticated ? children : <Navigate to="/login" replace />;
}

// Tab loading fallback - lightweight spinner
const TabLoadingFallback = () => (
  <div style={{
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
    height: '100%',
    color: 'var(--color-text-sub)',
  }}>
    <span style={{ fontSize: '24px', animation: 'pulse 1.5s ease-in-out infinite' }}>🍃</span>
  </div>
);

// Main app with tabs
function MainApp() {
  const [activeTab, setActiveTab] = useState('query');
  const languages = useSelector(() => languages$.items.get());
  const languagesLoading = useSelector(() => languages$.isLoading.get());

  // Show onboarding if no languages and not loading
  const showOnboarding = !languagesLoading && languages?.length === 0;

  // Fetch all data on mount
  useEffect(() => {
    fetchLanguages(); // Must fetch languages to determine if onboarding is needed
    fetchDecks();
    fetchCards();
    fetchXPData();
    fetchSettings();
  }, []);

  const renderContent = () => {
    switch (activeTab) {
      case 'query':
        return <ChatTab key="query" tabMode="query" />;
      case 'course':
        return <ChatTab key="course" tabMode="course" />;
      case 'discovery':
        return <DiscoveryTab onTabChange={setActiveTab} />;
      case 'proofread':
        return <ProofreadTab />;
      case 'review':
        return <ReviewTab />;
      case 'profile':
        return <ProfileTab onTabChange={setActiveTab} />;
      default:
        return <ChatTab key="query" tabMode="query" />;
    }
  };

  return (
    <ChatProvider>
      {showOnboarding && (
        <Suspense fallback={<TabLoadingFallback />}>
          <Onboarding />
        </Suspense>
      )}
      <div className="app-shell">
        <main className="main-content">
          <Suspense fallback={<TabLoadingFallback />}>
            {renderContent()}
          </Suspense>
        </main>
        <Navigation activeTab={activeTab} onTabChange={setActiveTab} />
      </div>
      {/* Global XP Animation Portal */}
      <XPAnimationPortal />
    </ChatProvider>
  );
}

// Component to track route changes for Hotjar and Sentry
function RouteTracker() {
  const location = useLocation();

  useEffect(() => {
    // Track page view on route change (Hotjar)
    trackPageView(location.pathname + location.search);

    // Update Sentry context with current route
    Sentry.setContext('route', {
      pathname: location.pathname,
      search: location.search,
      hash: location.hash,
    });

    // Add breadcrumb for navigation
    Sentry.addBreadcrumb({
      category: 'navigation',
      message: `Navigated to ${location.pathname}`,
      level: 'info',
    });
  }, [location]);

  return null;
}

// Helper function to capture access parameter for acquisition tracking
function captureAccessParameter() {
  if (typeof window === 'undefined') return;

  try {
    const params = new URLSearchParams(window.location.search);
    const access = params.get('access');

    if (access && /^[a-zA-Z0-9-_]+$/.test(access)) {
      sessionStorage.setItem('mellow_pending_access', access);
      console.log('[Access] Captured pending access:', access);

      // Clean URL (remove access parameter)
      const url = new URL(window.location.href);
      url.searchParams.delete('access');
      window.history.replaceState({}, '', url.toString());
    }
  } catch (error) {
    console.warn('[Access] Failed to capture access parameter:', error);
    // Non-critical - graceful degradation
  }
}

function AppContent() {
  const isAuthenticated = useSelector(() => auth$.isAuthenticated.get());
  useKeyboardScrollAssist();

  // Initialize analytics and native features on mount
  useEffect(() => {
    // Analytics
    initHotjar();
    initClarity();
    initPostHog();
    initAmplitude();
    initGA4();
    initFeaturebase(); // Feedback widget

    // Restore "hide feedback" state if still within the 30-min window
    try {
      const hiddenUntil = parseInt(localStorage.getItem('mellow-feedback-hidden-until'), 10);
      if (hiddenUntil && Date.now() < hiddenUntil) {
        document.body.classList.add('feedback-temporarily-hidden');
        const remaining = hiddenUntil - Date.now();
        setTimeout(() => {
          document.body.classList.remove('feedback-temporarily-hidden');
          try { localStorage.removeItem('mellow-feedback-hidden-until'); } catch {}
        }, remaining);
      } else {
        localStorage.removeItem('mellow-feedback-hidden-until');
      }
    } catch { /* localStorage unavailable */ }

    // Capture cross-domain tracking params from landing page
    captureCrossDomainParams();

    // Capture access parameter for acquisition tracking
    captureAccessParameter();

    // Native platform features (Capacitor)
    const initNative = async () => {
      await initNativeFeatures();
      await initDeepLinks();
      // Hide splash screen after initialization
      await hideSplashScreen();
    };
    initNative();
  }, []);

  // Initialize push notifications when user is authenticated
  useEffect(() => {
    if (isAuthenticated) {
      initPushNotifications();
    }
  }, [isAuthenticated]);

  // Identify user in Featurebase for feedback widget
  useEffect(() => {
    const user = auth$.user.get();
    if (isAuthenticated && user) {
      identifyFeaturebaseUser(user.id, {
        email: user.email,
        name: user.username || user.email?.split('@')[0],
        profilePicture: user.avatar_url,
      });
    } else {
      clearFeaturebaseIdentity();
    }
  }, [isAuthenticated]);

  const isMellowPwaDismissed = () => {
    try {
      const until = parseInt(localStorage.getItem('mellow-pwa-dismissed'), 10);
      return until && Date.now() < until;
    } catch { return false; }
  };
  const showPwaInstall = isAuthenticated && !Capacitor.isNativePlatform() && !isMellowPwaDismissed();
  const handleMellowPwaChoice = () => {
    try {
      localStorage.setItem('mellow-pwa-dismissed', String(Date.now() + 30 * 24 * 60 * 60 * 1000));
    } catch {}
  };

  // Page loading fallback for route transitions
  const PageLoadingFallback = (
    <div className="loading-screen">
      <span className="loading-icon">🍃</span>
      <span className="loading-text">Loading...</span>
    </div>
  );

  return (
    <BrowserRouter>
      <RouteTracker />
      <Suspense fallback={PageLoadingFallback}>
        <Routes>
          <Route
            path="/login"
            element={isAuthenticated ? <Navigate to="/" replace /> : <LoginPage />}
          />
          <Route
            path="/payment-success"
            element={
              <ProtectedRoute>
                <PaymentSuccess />
              </ProtectedRoute>
            }
          />
          {/* Public shared chat view - no auth required */}
          <Route
            path="/shared/chat/:token"
            element={<SharedMellowChatView />}
          />
          <Route
            path="/"
            element={
              <ProtectedRoute>
                <MainApp />
              </ProtectedRoute>
            }
          />
        </Routes>
      </Suspense>
      <Analytics />
      {showPwaInstall && (
        <PwaInstall
          manifestUrl="/manifest.webmanifest"
          icon="/icons/icon-192x192.png"
          onPwaUserChoiceResultEvent={handleMellowPwaChoice}
          onPwaInstallSuccessEvent={handleMellowPwaChoice}
        />
      )}

      <style>{`
        .app-shell {
          height: 100dvh;
          width: 100vw;
          overflow: hidden;
          background-color: var(--color-bg);
          display: flex;
          flex-direction: column;
          padding-top: env(safe-area-inset-top);
          /* Landscape: respect left/right safe areas (camera notch on iPhone) */
          padding-left: env(safe-area-inset-left);
          padding-right: env(safe-area-inset-right);
        }

        .main-content {
          flex: 1;
          overflow: hidden;
          width: 100%;
          position: relative;
        }

        .loading-screen {
          min-height: 100dvh;
          display: flex;
          flex-direction: column;
          align-items: center;
          justify-content: center;
          gap: var(--spacing-md);
          background-color: var(--color-bg);
        }

        .loading-icon {
          font-size: 48px;
          animation: pulse 1.5s ease-in-out infinite;
        }

        .loading-text {
          font-size: 16px;
          font-weight: 700;
          color: var(--color-text-sub);
        }

        @keyframes pulse {
          0%, 100% { transform: scale(1); opacity: 1; }
          50% { transform: scale(1.1); opacity: 0.7; }
        }

        @media (min-width: 768px) {
          .app-shell {
            flex-direction: row-reverse;
          }

          /* main-content takes remaining width via flex: 1 — no margin needed since nav is in-flow */
          .main-content {
            width: auto;
          }
        }
      `}</style>
    </BrowserRouter>
  );
}

// Wrap with ErrorBoundary and Suspense
export default function App() {
  return (
    <ErrorBoundary>
      <Suspense fallback={
        <div className="loading-screen">
          <span className="loading-icon">🍃</span>
          <span className="loading-text">Loading...</span>
        </div>
      }>
        <AppContent />
      </Suspense>
    </ErrorBoundary>
  );
}
